import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const LanguageContext = createContext();

const availableLanguages = ['ba', 'en', 'fr', 'es', 'it', 'ru', 'ar', 'de', 'pt', 'tr', 'zh', 'hi', 'ur'];

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract language from URL path
  const getLanguageFromPath = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    return availableLanguages.includes(firstSegment) ? firstSegment : 'ba';
  };

  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Check URL first, then localStorage, then default to 'ba'
    const urlLang = getLanguageFromPath();
    const savedLang = localStorage.getItem('preferred_language');
    return urlLang || savedLang || 'ba';
  });

  // Get path without language prefix
  const getPathWithoutLanguage = (path) => {
    const segments = path.split('/').filter(Boolean);
    if (availableLanguages.includes(segments[0])) {
      return '/' + segments.slice(1).join('/');
    }
    return path;
  };

  // Get path with language prefix
  const getPathWithLanguage = (path, lang) => {
    const cleanPath = getPathWithoutLanguage(path || '/');
    if (lang === 'ba') {
      return cleanPath || '/';
    }
    return `/${lang}${cleanPath === '/' ? '' : cleanPath}`;
  };

  // Change language and update URL
  const changeLanguage = (newLanguage) => {
    if (newLanguage === currentLanguage) return;
    
    setCurrentLanguage(newLanguage);
    localStorage.setItem('preferred_language', newLanguage);
    
    // Update URL with new language
    const currentPathWithoutLang = getPathWithoutLanguage(location.pathname);
    const newPath = getPathWithLanguage(currentPathWithoutLang, newLanguage);
    navigate(newPath + location.search + location.hash, { replace: true });
  };

  // Update current language when URL changes
  useEffect(() => {
    const urlLang = getLanguageFromPath();
    if (urlLang !== currentLanguage) {
      setCurrentLanguage(urlLang);
      localStorage.setItem('preferred_language', urlLang);
    }
  }, [location.pathname]);

  // Redirect /ba URLs to the canonical unprefixed BA URL
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const hasLanguagePrefix = availableLanguages.includes(pathSegments[0]);
    
    if (hasLanguagePrefix && pathSegments[0] === 'ba') {
      const newPath = getPathWithoutLanguage(location.pathname);
      navigate(newPath + location.search + location.hash, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  const value = {
    currentLanguage,
    changeLanguage,
    getPathWithLanguage,
    getPathWithoutLanguage,
    availableLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
